import { useState } from 'react';
import type { ExpenseCostSplit } from '../../api';
import type { Aircraft, User } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import {
  formatIssuePerformerLabel,
  issuePerformerError,
  performerFromAircraft,
  type MaintenancePerformerType,
} from '../../utils/aircraftMaintenance';
import { parseIssueCost } from '../../utils/issueDisplay';
import IssueResolveFields from '../IssueResolveFields/IssueResolveFields';
import ModalPortal from '../modal/ModalPortal';
import './ResolveIssueModal.css';

interface Props {
  aircraft?: Aircraft | null;
  members: User[];
  currentUserId?: number;
  onClose: () => void;
  onConfirm: (data: {
    resolved_date: string;
    resolved_by: string;
    cost: number;
    cost_split: ExpenseCostSplit;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
  }) => void | Promise<void>;
}

const getToday = () => new Date().toISOString().split('T')[0];

const ResolveIssueModal = ({
  aircraft,
  members,
  currentUserId = 0,
  onClose,
  onConfirm,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const initial = performerFromAircraft(aircraft);
  const [resolvedDate, setResolvedDate] = useState(getToday);
  const [performerType, setPerformerType] =
    useState<MaintenancePerformerType>(initial.type);
  const [performerName, setPerformerName] = useState(initial.name);
  const [cost, setCost] = useState('0');
  const [costSplit, setCostSplit] = useState<ExpenseCostSplit>('shares');
  const [payerUserId, setPayerUserId] = useState(currentUserId);

  const handleConfirm = () => {
    if (submitting) return;
    if (!resolvedDate) {
      showError('Укажите дату устранения');
      return;
    }
    const performerError = issuePerformerError(performerType, performerName);
    if (performerError) {
      showError(performerError);
      return;
    }
    const parsed = parseIssueCost(cost);
    if (!parsed.ok) {
      showError(parsed.error);
      return;
    }
    const paid = parsed.value > 0;
    const split: ExpenseCostSplit =
      paid && costSplit === 'personal' ? 'personal' : 'shares';
    if (split === 'personal' && !payerUserId) {
      showError('Укажите, кто оплатит устранение');
      return;
    }

    void runSubmit(() =>
      onConfirm({
        resolved_date: resolvedDate,
        resolved_by: formatIssuePerformerLabel(performerType, performerName),
        cost: parsed.value,
        cost_split: split,
        payer_ids: split === 'personal' ? [payerUserId] : undefined,
      })
    );
  };

  return (
    <ModalPortal>
    <div className="modal-overlay" onClick={onClose}>
      {feedbackNode}
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Устранение неисправности</h3>
        <div className="modal-body">
          <label>Дата</label>
          <input
            type="date"
            value={resolvedDate}
            onChange={(e) => setResolvedDate(e.target.value)}
          />
          <IssueResolveFields
            members={members}
            performerType={performerType}
            performerName={performerName}
            cost={cost}
            costSplit={costSplit}
            payerUserId={payerUserId}
            onPerformerTypeChange={setPerformerType}
            onPerformerNameChange={setPerformerName}
            onCostChange={setCost}
            onCostSplitChange={(value) => {
              setCostSplit(value);
              if (value === 'personal' && !payerUserId && currentUserId) {
                setPayerUserId(currentUserId);
              }
            }}
            onPayerUserIdChange={setPayerUserId}
          />
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? '…' : 'Устранить'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default ResolveIssueModal;
